import { useEffect, useState, useCallback } from 'react'
import {
  Layout, Plus, X, Trash2, Loader2, Eye, EyeOff,
  ArrowUp, ArrowDown, Image, Video, Type,
  Bold, Heading1, Heading2, Heading3, Heading4, Quote,
  Smile, Save
} from 'lucide-react'
import client from '../api/client'

interface LandingBlock {
  id: number
  section: string
  block_key: string
  block_type: string
  content: string | null
  media_url: string | null
  media_alt: string | null
  icon_name: string | null
  format_type: string | null
  is_bold: boolean
  extra_data: string | null
  sort_order: number
  is_visible: boolean
}

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero (Главный экран)',
  services: 'Сервисы',
  features: 'Преимущества',
  stats: 'Статистика',
  pricing: 'Тарифы',
  cta: 'CTA (Призыв к действию)',
  seo_text: 'SEO-текст',
  faq: 'FAQ (Вопросы-ответы)',
  footer: 'Footer',
}

const SECTION_COLORS: Record<string, string> = {
  hero: 'from-indigo-500/10 to-purple-600/10 border-indigo-500/20',
  services: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20',
  features: 'from-amber-500/10 to-orange-500/10 border-amber-500/20',
  stats: 'from-blue-500/10 to-cyan-500/10 border-blue-500/20',
  pricing: 'from-purple-500/10 to-violet-500/10 border-purple-500/20',
  cta: 'from-pink-500/10 to-rose-500/10 border-pink-500/20',
  seo_text: 'from-teal-500/10 to-emerald-500/10 border-teal-500/20',
  faq: 'from-yellow-500/10 to-amber-500/10 border-yellow-500/20',
  footer: 'from-gray-500/10 to-slate-500/10 border-gray-500/20',
}

const FORMAT_OPTIONS = [
  { value: 'p', label: 'Текст', icon: Type },
  { value: 'strong', label: 'Просто жирный', icon: Bold },
  { value: 'h1', label: 'H1', icon: Heading1 },
  { value: 'h2', label: 'H2', icon: Heading2 },
  { value: 'h3', label: 'H3', icon: Heading3 },
  { value: 'h4', label: 'H4', icon: Heading4 },
  { value: 'blockquote', label: 'Цитата', icon: Quote },
]

const COMMON_ICONS = [
  'Sparkles', 'MessageSquare', 'Image', 'Video', 'Music', 'Bot', 'Code',
  'Star', 'Shield', 'Zap', 'Globe', 'Brain', 'Users', 'Check',
  'ArrowRight', 'Heart', 'Award', 'Rocket', 'Target', 'Coins', 'Gift',
  'Settings', 'Search', 'Send', 'Share2', 'ShoppingCart', 'Tag',
  'ThumbsUp', 'Trophy', 'Upload', 'Wallet', 'Key', 'Lock',
]

// ——— Edit buffer for unsaved changes ———
// Each edited block gets a local draft; saved blocks merge back into blocks state
type DraftBlock = Partial<LandingBlock> & { id: number }

export default function LandingCms() {
  const [blocks, setBlocks] = useState<LandingBlock[]>([])
  const [loading, setLoading] = useState(true)
  const [iconPickerOpen, setIconPickerOpen] = useState<number | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [savingId, setSavingId] = useState<number | null>(null)

  // Local drafts – map block.id → DraftBlock
  const [drafts, setDrafts] = useState<Record<number, DraftBlock>>({})

  const [newBlock, setNewBlock] = useState({
    section: 'hero',
    block_key: '',
    block_type: 'text',
    content: '',
    media_url: '',
    media_alt: '',
    icon_name: '',
    format_type: 'p',
    is_bold: false,
    extra_data: '',
    is_visible: true,
    sort_order: 0,
  })
  const [creating, setCreating] = useState(false)

  const fetchBlocks = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await client.get('/admin/landing/blocks')
      setBlocks(data || [])
    } catch {
      setBlocks([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchBlocks() }, [fetchBlocks])

  // ——— Draft helpers ———
  const saveDraft = (id: number, field: string, value: any) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { id }), [field]: value },
    }))
  }

  const getDraft = (block: LandingBlock): any => {
    const draft = drafts[block.id]
    return draft || block
  }

  const saveBlock = async (id: number, updates: Partial<LandingBlock>) => {
    setSavingId(id)
    try {
      await client.put(`/admin/landing/blocks/${id}`, updates)
      // Clear draft & update local state
      setDrafts((prev) => {
        const { [id]: _, ...rest } = prev
        return rest
      })
      setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)))
    } catch { /* ignore */ } finally {
      setSavingId(null)
    }
  }

  const saveDraftAndPersist = async (block: LandingBlock, field: string, value: any) => {
    saveDraft(block.id, field, value)
    await saveBlock(block.id, { [field]: value })
  }

  const deleteBlock = async (id: number) => {
    if (!window.confirm('Удалить блок?')) return
    try {
      await client.delete(`/admin/landing/blocks/${id}`)
      setBlocks((prev) => prev.filter((b) => b.id !== id))
    } catch { /* ignore */ }
  }

  const moveBlock = async (id: number, direction: 'up' | 'down') => {
    const idx = blocks.findIndex((b) => b.id === id)
    if (idx === -1) return
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === blocks.length - 1) return
    const newBlocks = [...blocks]
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newBlocks[idx], newBlocks[swapIdx]] = [newBlocks[swapIdx], newBlocks[idx]]
    try {
      await client.post('/admin/landing/blocks/reorder', { ids: newBlocks.map((b) => b.id) })
      setBlocks(newBlocks)
    } catch { /* ignore */ }
  }

  const createBlock = async () => {
    if (!newBlock.block_key.trim()) return
    setCreating(true)
    try {
      const { data } = await client.post('/admin/landing/blocks', newBlock)
      setBlocks((prev) => [...prev, data])
      setShowAddForm(false)
      setNewBlock({ section: 'hero', block_key: '', block_type: 'text', content: '', media_url: '', media_alt: '', icon_name: '', format_type: 'p', is_bold: false, extra_data: '', is_visible: true, sort_order: blocks.length })
    } catch { /* ignore */ } finally {
      setCreating(false)
    }
  }

  const groupedBlocks: Record<string, LandingBlock[]> = {}
  blocks.forEach((b) => {
    if (!groupedBlocks[b.section]) groupedBlocks[b.section] = []
    groupedBlocks[b.section].push(b)
  })

  // ——— Preview ———
  const renderPreview = (block: LandingBlock) => {
    const draft = getDraft(block)
    const fmt = draft.format_type || 'p'
    const bold = draft.is_bold
    const content = draft.content || ''
    const icon = draft.icon_name
    const hasMedia = draft.media_url && draft.media_url.trim()

    let className = ''
    if (fmt === 'h1') className = 'text-2xl font-bold'
    else if (fmt === 'h2') className = 'text-xl font-bold'
    else if (fmt === 'h3') className = 'text-lg font-semibold'
    else if (fmt === 'h4') className = 'text-base font-semibold'
    else if (fmt === 'blockquote') className = 'italic border-l-2 border-indigo-400/30 pl-3 text-gray-400'
    else if (fmt === 'strong') className = 'font-bold'
    else className = `text-sm ${bold ? 'font-bold' : ''}`

    const Tag = fmt === 'h1' ? 'h1' : fmt === 'h2' ? 'h2' : fmt === 'h3' ? 'h3' : fmt === 'h4' ? 'h4' : fmt === 'blockquote' ? 'blockquote' : fmt === 'strong' ? 'strong' : 'p'

    return (
      <div className="mb-2 p-2 rounded-lg bg-white/[0.02] border border-white/5">
        <div className="text-[10px] text-gray-600 mb-1">Предпросмотр:</div>
        <Tag className={`text-gray-200 ${className}`}>
          {icon && <span className="mr-1.5 opacity-60">[{icon}]</span>}
          {content}
        </Tag>
        {hasMedia && (
          <div className="mt-2 flex items-center gap-2">
            <Image className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="text-[10px] text-gray-500 truncate">{draft.media_url}</span>
            {draft.media_alt && <span className="text-[10px] text-gray-600">— {draft.media_alt}</span>}
          </div>
        )}
      </div>
    )
  }

  // ——— Icon picker ———
  const renderIconPicker = (block: LandingBlock) => {
    const draft = getDraft(block)
    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setIconPickerOpen(iconPickerOpen === block.id ? null : block.id) }}
          className={`p-1.5 rounded-md text-[10px] transition-all ${draft.icon_name ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`}
          title="Выбрать иконку"
        >
          <Smile className="w-3.5 h-3.5" />
        </button>
        {iconPickerOpen === block.id && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-[#1a1a2e] border border-white/10 rounded-xl p-2 shadow-2xl w-64" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-gray-400">Иконка блока</span>
              {draft.icon_name && (
                <button type="button" onClick={() => { saveDraftAndPersist(block, 'icon_name', null); setIconPickerOpen(null) }} className="text-[10px] text-red-400 hover:text-red-300">Убрать</button>
              )}
            </div>
            <div className="grid grid-cols-8 gap-0.5 max-h-36 overflow-y-auto">
              {COMMON_ICONS.map((iconName) => (
                <button key={iconName} type="button" onClick={() => { saveDraftAndPersist(block, 'icon_name', iconName); setIconPickerOpen(null) }}
                  className={`p-1 rounded-md text-[9px] transition-all ${draft.icon_name === iconName ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'}`} title={iconName}
                >{iconName.slice(0, 3)}</button>
              ))}
            </div>
            <input
              type="text"
              value={draft.icon_name || ''}
              onChange={(e) => saveDraftAndPersist(block, 'icon_name', e.target.value)}
              placeholder="Или введите название из Lucide..."
              className="input-field w-full mt-1.5 text-[9px]"
            />
          </div>
        )}
      </div>
    )
  }

  // ——— Editor ———
  const renderBlockEditor = (block: LandingBlock) => {
    const draft = getDraft(block)
    const isSaving = savingId === block.id
    const hasDraft = !!drafts[block.id]

    return (
      <div key={block.id} className={`glass-card p-4 border transition-all ${block.is_visible ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-500 text-xs">{block.block_key}</span>
            <span className="text-[10px] text-gray-600">· {block.block_type} · {block.format_type || 'p'} · #{block.sort_order}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isSaving && <Loader2 className="w-3 h-3 text-indigo-400 animate-spin" />}
            {hasDraft && <span className="text-[10px] text-amber-400">• не сохранено</span>}
            <button type="button" onClick={(e) => { e.preventDefault(); saveBlock(block.id, drafts[block.id] as any) }} disabled={!hasDraft}
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all disabled:opacity-30" title="Сохранить">
              <Save className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={(e) => { e.preventDefault(); saveBlock(block.id, { is_visible: !block.is_visible }) }}
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all" title={block.is_visible ? 'Скрыть' : 'Показать'}>
              {block.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
            <button type="button" onClick={(e) => { e.preventDefault(); moveBlock(block.id, 'up') }}
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all" title="Вверх"><ArrowUp className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); moveBlock(block.id, 'down') }}
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200 transition-all" title="Вниз"><ArrowDown className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); deleteBlock(block.id) }}
              className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-all" title="Удалить"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Formatting toolbar */}
        <div className="flex flex-wrap items-center gap-1 mb-3 pb-2 border-b border-white/5">
          <div className="flex items-center gap-0.5 bg-black/20 rounded-lg p-0.5">
            {FORMAT_OPTIONS.map((opt) => (
              <button key={opt.value} type="button"
                onClick={(e) => { e.preventDefault(); saveDraftAndPersist(block, 'format_type', opt.value) }}
                className={`p-1.5 rounded-md text-[10px] transition-all ${(draft.format_type || 'p') === opt.value ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} title={opt.label}>
                <opt.icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          <span className="w-px h-5 bg-white/5 mx-0.5" />
          <button type="button" onClick={(e) => { e.preventDefault(); saveDraftAndPersist(block, 'is_bold', !draft.is_bold) }}
            className={`p-1.5 rounded-md text-[10px] transition-all ${draft.is_bold ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} title="Жирный">
            <Bold className="w-3.5 h-3.5" />
          </button>
          {renderIconPicker(block)}
          <span className="w-px h-5 bg-white/5 mx-0.5" />
          <div className="flex items-center gap-0.5 bg-black/20 rounded-lg p-0.5">
            <button type="button" onClick={(e) => { e.preventDefault(); saveDraftAndPersist(block, 'block_type', 'text') }}
              className={`p-1.5 rounded-md text-[10px] transition-all ${draft.block_type === 'text' || draft.block_type === 'rich' ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} title="Текст"><Type className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); saveDraftAndPersist(block, 'block_type', 'image') }}
              className={`p-1.5 rounded-md text-[10px] transition-all ${draft.block_type === 'image' ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} title="Изображение"><Image className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); saveDraftAndPersist(block, 'block_type', 'video') }}
              className={`p-1.5 rounded-md text-[10px] transition-all ${draft.block_type === 'video' ? 'bg-indigo-500/20 text-indigo-300' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} title="Видео"><Video className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Preview */}
        {renderPreview(block)}

        {/* Content editors — persist on blur, not on every keystroke */}
        <div className="space-y-2">
          <textarea
            defaultValue={block.content || ''}
            onBlur={(e) => { if (e.target.value !== (block.content || '')) saveDraftAndPersist(block, 'content', e.target.value) }}
            rows={3}
            placeholder="Текстовое содержимое блока..."
            className="input-field w-full resize-none text-xs"
          />
          <div className="flex gap-2 items-start">
            <button type="button" onClick={(e) => { e.preventDefault(); document.getElementById(`fu-${block.id}`)?.click() }}
              className="px-3 py-2 rounded-xl text-xs bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 transition-all flex items-center gap-2 shrink-0">
              <Image className="w-3.5 h-3.5" />Загрузить
            </button>
            <input id={`fu-${block.id}`} type="file"
              accept={block.block_type === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'image/jpeg,image/png,image/webp,image/gif'}
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                const fd = new FormData(); fd.append('file', file)
                try {
                  const { data } = await client.post(block.block_type === 'video' ? '/upload/video' : '/upload/image', fd)
                  await saveBlock(block.id, { media_url: data.url })
                } catch {}
              }}
            />
            <input type="text" defaultValue={block.media_url || ''}
              onBlur={(e) => { if (e.target.value !== (block.media_url || '')) saveBlock(block.id, { media_url: e.target.value }) }}
              placeholder="или ссылка" className="input-field flex-1 text-xs" />
            {block.block_type === 'image' && block.media_url && (
              <div className="w-10 h-10 rounded-lg bg-white/5 overflow-hidden shrink-0">
                <img src={block.media_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            {block.block_type === 'video' && block.media_url && (
              <div className="w-10 h-10 rounded-lg bg-white/5 overflow-hidden shrink-0 flex items-center justify-center"><Video className="w-5 h-5 text-gray-500" /></div>
            )}
          </div>
          <input type="text" defaultValue={block.media_alt || ''}
            onBlur={(e) => { if (e.target.value !== (block.media_alt || '')) saveBlock(block.id, { media_alt: e.target.value }) }}
            placeholder="Alt-текст / описание медиа" className="input-field w-full text-[10px]" />
          <input type="text" defaultValue={(() => { try { const d = JSON.parse(block.extra_data || '{}'); return d.href || '' } catch { return '' } })()}
            onBlur={(e) => { saveBlock(block.id, { extra_data: JSON.stringify({ href: e.target.value || null }) }) }}
            placeholder="Ссылка (URL): https://..." className="input-field w-full text-[10px]" />
        </div>
      </div>
    )
  }

  // ================== RENDER ==================
  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
  }

  return (
    <div className="space-y-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
          <Layout className="w-4 h-4 text-indigo-400" /> Редактор лендинга
        </h3>
        <button type="button" onClick={(e) => { e.preventDefault(); setShowAddForm(!showAddForm) }}
          className="gradient-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" />Добавить блок
        </button>
      </div>

      {showAddForm && (
        <div className="glass-card p-4 space-y-3 border border-indigo-500/30 relative" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-indigo-300">Новый блок</h4>
            <button type="button" onClick={(e) => { e.preventDefault(); setShowAddForm(false) }}
              className="p-1 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-200"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Секция</label>
              <select value={newBlock.section} onChange={(e) => setNewBlock((p) => ({ ...p, section: e.target.value }))} className="input-field w-full text-xs">
                {Object.entries(SECTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Тип</label>
              <select value={newBlock.block_type} onChange={(e) => setNewBlock((p) => ({ ...p, block_type: e.target.value }))} className="input-field w-full text-xs">
                <option value="text">Текст</option>
                <option value="image">Изображение</option>
                <option value="video">Видео</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-600 mb-1">Ключ блока</label>
              <input type="text" value={newBlock.block_key} onChange={(e) => setNewBlock((p) => ({ ...p, block_key: e.target.value }))} placeholder="hero_title" className="input-field w-full text-xs" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] text-gray-600 mb-1">Содержимое</label>
              <textarea value={newBlock.content} onChange={(e) => setNewBlock((p) => ({ ...p, content: e.target.value }))} rows={3} className="input-field w-full resize-none text-xs" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Формат</label>
              <select value={newBlock.format_type} onChange={(e) => setNewBlock((p) => ({ ...p, format_type: e.target.value }))} className="input-field w-full text-xs">
                {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Иконка</label>
              <input type="text" value={newBlock.icon_name} onChange={(e) => setNewBlock((p) => ({ ...p, icon_name: e.target.value }))} placeholder="Sparkles" className="input-field w-full text-xs" />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={(e) => { e.preventDefault(); createBlock() }} disabled={creating || !newBlock.block_key.trim()}
              className="gradient-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 disabled:opacity-40">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {creating ? 'Создание...' : 'Создать блок'}
            </button>
          </div>
        </div>
      )}

      {Object.entries(SECTION_LABELS).map(([sectionKey, sectionLabel]) => {
        const sectionBlocks = groupedBlocks[sectionKey]
        if (!sectionBlocks || sectionBlocks.length === 0) return null
        return (
          <div key={sectionKey} className={`glass-card p-4 border ${SECTION_COLORS[sectionKey] || 'border-white/10'}`} onClick={(e) => e.stopPropagation()}>
            <h4 className="text-xs font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${sectionKey === 'hero' ? 'bg-indigo-400' : 'bg-gray-500'}`} />
              {sectionLabel} <span className="text-[10px] text-gray-600 font-normal">({sectionBlocks.length})</span>
            </h4>
            <div className="space-y-3">{sectionBlocks.map(renderBlockEditor)}</div>
          </div>
        )
      })}

      {blocks.length === 0 && (
        <div className="glass-card p-8 text-center">
          <Layout className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Нет блоков лендинга.</p>
        </div>
      )}
    </div>
  )
}
